import React, { useState, useEffect } from 'react';
import HeaderMobile from './HeaderMobile.js';
import Header from './Header.js';
import Sidebar from './Sidebar.js';
import Footer from './Footer.js';
import { BASE_URL } from "./Constants.js";
import * as XLSX from 'xlsx';

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [areaWiseData, setAreaWiseData] = useState([]);
  const [noDataFound, setNoDataFound] = useState(false);
  const [approvedUniformsIssued, setApprovedUniformsIssued] = useState(0);
  const [approvedUniformsCost, setApprovedUniformsCost] = useState(0);
  const [pendingUniformsIssued, setPendingUniformsIssued] = useState(0);
  const [pendingUniformsCost, setPendingUniformsCost] = useState(0);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [genderFilter, setGenderFilter] = useState('');
  const [dressTypeFilter, setDressTypeFilter] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [sizeFilter, setSizeFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [areaWiseStats, setAreaWiseStats] = useState({
    totalIssued: 0,
    totalCost: 0,
    pendingIssued: 0,
    pendingCost: 0
  });
  const [selectedArea, setSelectedArea] = useState('');
  const [areas, setAreas] = useState([]);


  const getDefaultDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 2);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const format = (date) => date.toISOString().split('T')[0];
    return {
      from: format(firstDay),
      to: format(lastDay),
    };
  };

  const defaultDates = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaultDates.from);
  const [toDate, setToDate] = useState(defaultDates.to);

  const handleToggleSidebar = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarOpen(prev => !prev);
    }
  };
  const uniqueSizes = [...new Set(areaWiseData.map(item => item.dress_size_name))];
  const uniqueColors = [...new Set(areaWiseData.map(item => item.dress_color_name))];
  const sortedSizes = uniqueSizes.sort();
  const sortedColors = uniqueColors.sort();
  const handleFilter = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both From and To dates.");
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
    const params = `?from_date=${fromDate}&to_date=${toDate}${selectedLocation && selectedLocation !== 'All' ? `&area=${selectedLocation}` : ''}`;

    try {
      setLoading(true);

      const approvedRes = await fetch(`${BASE_URL}total_approved_uniforms_issued_by_user${params}`, { headers });
      const approvedData = await approvedRes.json();
      if (approvedData.status) {
        setApprovedUniformsIssued(approvedData.data.total_issued);
        setApprovedUniformsCost(approvedData.data.total_cost);
      }

      const pendingRes = await fetch(`${BASE_URL}total_pending_uniforms_issued_by_user${params}`, { headers });
      const pendingData = await pendingRes.json();
      if (pendingData.status) {
        setPendingUniformsIssued(pendingData.data.total_issued);
        setPendingUniformsCost(pendingData.data.total_cost);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    try {
      const res = await fetch(`${BASE_URL}get_locations`, { headers });
      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setLocations(data.data);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  useEffect(() => {
    fetchLocations();
    handleFilter();
  }, []);

  const fetchAreas = async () => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    try {
      const res = await fetch(`${BASE_URL}get_areas`, { headers });
      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setAreas(data.data);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  useEffect(() => {
    fetchAreas();
    handleFilter();
  }, []);

  const fetchAreaWiseData = async (areaId = '') => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    let url = `${BASE_URL}area_wise_uniform_summary`;

    if (areaId) {
      url += `?area=${areaId}`;
    }

    try {
      const res = await fetch(url, { headers });
      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        setAreaWiseData(data.data);
        setNoDataFound(false);
      } else {
        setAreaWiseData([]);
        setNoDataFound(true);
      }
    } catch (err) {
      console.error("Error fetching area-wise stats:", err);
      setAreaWiseData([]);
      setNoDataFound(true);
    }
  };

  // ✅ Call on mount and whenever selectedArea changes (including when it's empty)
  useEffect(() => {
    fetchAreaWiseData(selectedArea);
  }, [selectedArea]);

  const filteredData = areaWiseData.filter((item) =>

    (genderFilter === '' || item.gender === genderFilter) &&
    (dressTypeFilter === '' || item.dress_type_name === dressTypeFilter) &&
    (sizeFilter === '' || item.dress_size_name === sizeFilter) &&
    (colorFilter === '' || item.dress_color_name === colorFilter)
  );

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);


  const exportToExcel = () => {
    if (filteredData.length === 0) {
      alert("No filtered data available to export.");
      return;
    }

    const worksheetData = filteredData.map((item, index) => ({
      '#': index + 1,
      'Area': item.area_name,
      'Gender': item.gender,
      'Dress Type': item.dress_type_name,
      'Size': item.dress_size_name,
      'Color': item.dress_color_name,
      'Quantity': item.quantity,
      // 'Consumed Quantity': item.consumed_qty,
      // 'Available Quantity': item.available_qty,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Uniform Summary');

    XLSX.writeFile(workbook, 'Filtered_Uniform_Summary_Report.xlsx');
  };



  return (
    <div className="page-wrapper d-flex flex-column min-vh-100">
      <div className="flex-grow-1">
        <HeaderMobile />
        <Sidebar isOpen={isSidebarOpen} />
        <div
          className="page-container"
          style={{
            marginLeft: isSidebarOpen && window.innerWidth >= 992 ? '300px' : '100px',
            transition: 'margin-left 0.3s ease',
          }}
        >
          <Header onToggleSidebar={handleToggleSidebar} isSidebarOpen={isSidebarOpen} />

          <div className="main-content">
            <div className="section__content section__content--p30">
              <div className="container-fluid">

                {/* Filters */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-control"
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                    >
                      <option value="All">All Locations</option>
                      {areas.map((loc, idx) => (
                        <option key={idx} value={loc.id}>
                          {loc.area}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <button className="btn btn-primary" onClick={handleFilter}>
                      Apply Filter
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="row m-t-25">
                  <div className="col-sm-12 col-lg-3">
                    <div className="overview-item overview-item--c1">
                      <div className="overview__inner">
                        <div className="overview-box clearfix">
                          <div className="text">
                            <h2>{approvedUniformsIssued}</h2>
                            <span>Total Approved Uniforms Issued</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6 col-lg-3">
                    <div className="overview-item overview-item--c2">
                      <div className="overview__inner">
                        <div className="overview-box clearfix">
                          <div className="text">
                            <h2>{approvedUniformsCost}</h2>
                            <span>Total Approved Uniforms Amount</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6 col-lg-3">
                    <div className="overview-item overview-item--c3">
                      <div className="overview__inner">
                        <div className="overview-box clearfix">
                          <div className="text">
                            <h2>{pendingUniformsIssued}</h2>
                            <span>Total Pending Uniforms Issued</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6 col-lg-3">
                    <div className="overview-item overview-item--c4">
                      <div className="overview__inner">
                        <div className="overview-box clearfix">
                          <div className="text">
                            <h2>{pendingUniformsCost || 0}</h2>
                            <span>Total Pending Uniforms Amount</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
                <hr className="my-4" />

                <div className="row mb-3">
                  <div className="col-md-2">
                    <select
                      className="form-control"
                      value={selectedArea}
                      onChange={(e) => setSelectedArea(e.target.value)}
                    >
                      <option value="">Select Area</option>
                      {areas.map((loc, idx) => (
                        <option key={idx} value={loc.id}>
                          {loc.area}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-control"
                      value={genderFilter}
                      onChange={(e) => setGenderFilter(e.target.value)}
                    >
                      <option value="">All Genders</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-control"
                      value={dressTypeFilter}
                      onChange={(e) => setDressTypeFilter(e.target.value)}
                    >
                      <option value="">All Dress Types</option>
                      <option value="Shirts">Shirts</option>
                      <option value="Trouser">Trouser</option>
                      <option value="Apron">Apron</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-control"
                      value={sizeFilter}
                      onChange={(e) => setSizeFilter(e.target.value)}
                    >
                      <option value="">All Sizes</option>
                      {sortedSizes.map((size, idx) => (
                        <option key={idx} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2">
                    <select
                      className="form-control"
                      value={colorFilter}
                      onChange={(e) => setColorFilter(e.target.value)}
                    >
                      <option value="">All Colors</option>
                      {sortedColors.map((color, idx) => (
                        <option key={idx} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>

                  <button className="btn btn-success" onClick={exportToExcel}>
                    Export to Excel
                  </button>
                </div>

                <>
                  {areaWiseData.length > 0 ? (
                    <div className="table-responsive mt-4">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Area</th>
                            <th>Gender</th>
                            <th>Dress Type</th>
                            <th>Size</th>
                            <th>Color</th>
                            <th>Quantity</th>
                            {/* <th>Consumed Quantity</th>
                            <th>Available Quantity</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((item, index) => (
                            <tr key={`${item.id}-${index}`}>
                              <td>{indexOfFirstItem + index + 1}</td>
                              <td>{item.area_name}</td>
                              <td>{item.gender}</td>
                              <td>{item.dress_type_name}</td>
                              <td>{item.dress_size_name}</td>
                              <td>{item.dress_color_name}</td>
                              <td>{item.quantity}</td>
                              {/* <td>{item.consumed_qty}</td>
                              <td>{item.available_qty}</td> */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    noDataFound && (
                      <div className="alert alert-warning mt-4" role="alert">
                        No data found for the selected area.
                      </div>
                    )
                  )}
                </>

                <br />
                <div
                  className="d-flex justify-content-between align-items-center mt-3 flex-wrap"
                  style={{ gap: '10px' }}
                >
                  <div>
                    Showing <strong>{indexOfFirstItem + 1}</strong> to{' '}
                    <strong>{Math.min(indexOfLastItem, filteredData.length)}</strong> of{' '}
                    <strong>{filteredData.length}</strong> entries
                  </div>

                  <nav>
                    <ul className="pagination mb-0" style={{ marginBottom: 0, display: 'flex', flexWrap: 'wrap' }}>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`} style={{ marginRight: 4 }}>
                        <button
                          className="page-link"
                          style={{ padding: '4px 10px', fontSize: 14 }}
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        >
                          &lt;
                        </button>
                      </li>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p =>
                          p === 1 ||
                          p === totalPages ||
                          (p >= currentPage - 2 && p <= currentPage + 2)
                        )
                        .map((pageNum, index, array) => {
                          const prevPage = array[index - 1];
                          const isEllipsis = prevPage && pageNum - prevPage > 1;
                          return (
                            <React.Fragment key={pageNum}>
                              {isEllipsis && (
                                <li className="page-item disabled" style={{ marginRight: 4 }}>
                                  <span className="page-link" style={{ padding: '4px 10px', fontSize: 14 }}>
                                    ...
                                  </span>
                                </li>
                              )}
                              <li
                                className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                                style={{ marginRight: 4 }}
                              >
                                <button
                                  className="page-link"
                                  style={{ padding: '4px 10px', fontSize: 14 }}
                                  onClick={() => setCurrentPage(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            </React.Fragment>
                          );
                        })}

                      <li
                        className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                        style={{ marginRight: 4 }}
                      >
                        <button
                          className="page-link"
                          style={{ padding: '4px 10px', fontSize: 14 }}
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        >
                          &gt;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
